import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Geometry, MultiPolygon, Polygon } from 'ol/geom';
import { Fill, Stroke, Style} from 'ol/style';
import { VesselNavAreasModel } from '../../src/models/vessel-nav-areas-model';
import { Feature } from 'ol';

const _PROJECTION = (<any>window)["appenv"].projection;

export interface NavAreasLayerColors
{
    navAreaBorderColor: string;
    fillColor: string;
}

export class NavAreasLayer extends EventTarget
{
    private _navAreasLayer : VectorLayer<VectorSource<Geometry>> | null = null;

    constructor(colors?: Partial<NavAreasLayerColors>)
    {
        super();
        this.setColors(colors || {});
    }

    get colors(): NavAreasLayerColors
    {
        return this._colors;
    }

    get layers(): VectorLayer<VectorSource<Geometry>>[]
    {
        return this._layers;
    }

    async initialize(): Promise<void>
    {
        this._vectorSource = new VectorSource();
        this._navAreasLayer = new VectorLayer({source: this._vectorSource, visible: false});
        this._layers.push(this._navAreasLayer);
        this._plotNavAreas();
    }

    setColors(colors: Partial<NavAreasLayerColors>): void
    {
        this.colors.navAreaBorderColor = colors.navAreaBorderColor || this.colors.navAreaBorderColor;
        this.colors.fillColor = colors.fillColor || this.colors.fillColor;

        if (this._vectorSource){
            this._clear();
            this._plotNavAreas();
        }
    }

    private _clear()
    {
        try {
            this._vectorSource!.clear();
        } catch (error) {
            console.warn(error);
        }
    }

    enable() {
        this._clear();
        this._plotNavAreas().then(() => this._navAreasLayer!.setVisible(true));
    }

    disable() {
        this._navAreasLayer!.setVisible(false);
        this._clear();
    }

    private _colors: NavAreasLayerColors = {
        navAreaBorderColor: "black",
        fillColor: "black"
    };

    private _layers: VectorLayer<VectorSource<Geometry>>[] = [];
    private _vectorSource: VectorSource<Geometry>|null = null;

    private async _plotNavAreas()
    {
        const features: Feature<Geometry>[] = [];
        const navAreas = VesselNavAreasModel!.areas;
        const navAreaBorderColor = this.colors.navAreaBorderColor || "black";
        const fillColor = this.colors.fillColor || "black";

        const areasLine = new Style({
            stroke: new Stroke({
                color: navAreaBorderColor, width: 1
            }),
            fill: new Fill({color: fillColor}),
        });

        navAreas!.forEach(area => {
            let feature : Feature<Geometry> | null = null;
            if (area.geometry!.type === "MultiPolygon") {
                feature = new Feature({geometry: new MultiPolygon(<number[][][][]>area.geometry!.coordinates)});
            }
            if (area.geometry!.type === "Polygon") {
                feature = new Feature({geometry: new Polygon(<number[][][]>area.geometry!.coordinates)});
            }
            if (feature) {
                feature.getGeometry()?.transform('EPSG:4326', _PROJECTION);
                feature.setStyle(areasLine);
                const name = `NavArea ${area.properties.id} - ${area.properties.coordinator}`;
                feature.set('text', name);
                feature.set('layer', "NavAreasLayer");
                features.push(feature!);
            }
        });

        this._vectorSource!.addFeatures(features);
    }
};